import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { NotificationSetting } from '../models/CompanyOnboardingModels';
import { User } from '../models/UsersModels';
import { AssignDeviceRequest } from '../models/UserDevicesModels';
import { DropdownItem } from '../components/Dropdown/DropdownModels';
import { DevicesReport } from '../models/DevicesReportModels';

interface AssignDeviceStateData {
  request: AssignDeviceRequest;
  deviceToAssign: DevicesReport | null;
  userToAssign: User | null;
  selectedDropdownUser: DropdownItem;
  selectedDropdownDevice: DropdownItem;
  deviceNumber: string;
  qrCodeNotificationSettings: NotificationSetting[];
  hasDistributionList: boolean;
}

const initialState: AssignDeviceStateData = {
  request: {
    userId: '',
    deviceId: '',
    notifyAdmin: true,
    notificationRecipient: 'ADMIN',
    notifyDistributionList: false,
  },
  deviceToAssign: null,
  userToAssign: null,
  selectedDropdownUser: { key: '', label: '', value: '', secondaryLabel: '' },
  selectedDropdownDevice: { key: '', label: '', value: '', secondaryLabel: '' },
  deviceNumber: '',
  qrCodeNotificationSettings: [],
  hasDistributionList: false,
};

export const AssignDevice = createSlice({
  name: 'assignDevice',
  initialState,
  reducers: {
    updateRequest: (state, action: PayloadAction<Partial<AssignDeviceRequest>>) => {
      state.request = {
        ...state.request,
        ...action.payload,
      };
    },
    setDeviceToAssign: (state, action: PayloadAction<DevicesReport>) => {
      state.deviceToAssign = action.payload;
    },
    setUserToAssign: (state, action: PayloadAction<User>) => {
      state.userToAssign = action.payload;
    },
    updateSelectedDropdownUser: (state, action: PayloadAction<DropdownItem>) => {
      state.selectedDropdownUser = action.payload;
    },
    updateSelectedDropdownDevice: (state, action: PayloadAction<DropdownItem>) => {
      state.selectedDropdownDevice = action.payload;
    },
    updateDeviceNumber: (state, action: PayloadAction<string>) => {
      state.deviceNumber = action.payload;
    },
    setNotificationSettings: (state, action: PayloadAction<NotificationSetting[]>) => {
      state.hasDistributionList = action.payload.some((setting) => setting.type === 'DISTRIBUTION_LIST');
      if (action.payload.some((setting) => setting.type === 'END_USER'))
        state.request.notificationRecipient = 'END_USER';
      state.qrCodeNotificationSettings = action.payload;
    },
    reset: () => initialState,
  },
});

export const {
  updateRequest,
  setDeviceToAssign,
  setUserToAssign,
  setNotificationSettings,
  updateSelectedDropdownUser,
  updateSelectedDropdownDevice,
  updateDeviceNumber,
  reset,
} = AssignDevice.actions;

export default AssignDevice.reducer;
